import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { views, entities } from "../services/api";
import { toast } from "../components/Toast";
import {
  BackIcon,
  PencilIcon,
  CheckIcon,
  ContainerIcon,
  CloseIcon,
  PlusIcon,
  GridViewIcon,
  HomeIcon,
  CameraIcon,
  UserIcon,
} from "../components/Icons";

function CustomViewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [availableContainers, setAvailableContainers] = useState([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [containersById, setContainersById] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRows, setEditRows] = useState("");
  const [editCols, setEditCols] = useState("");

  useEffect(() => {
    loadView();
    loadContainers();
  }, [id]);

  const loadView = async () => {
    try {
      const { data } = await views.get(id);
      setView(data);
    } catch (err) {
      console.error("Failed to load view:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadContainers = async () => {
    try {
      const { data } = await entities.list({
        entity_type: "container",
        limit: -1,
      });
      const byId = {};
      for (const container of data) {
        byId[container.id] = container;
      }
      setContainersById(byId);
    } catch (err) {
      console.error("Failed to load containers:", err);
    }
  };

  const loadAvailableContainers = async () => {
    setLoadingContainers(true);
    try {
      const { data } = await entities.list({
        entity_type: "container",
        limit: -1,
      });
      const byId = {};
      for (const container of data) {
        byId[container.id] = container;
      }
      setContainersById(byId);
      // Slots are a flat array of entity ids (padded with nulls);
      // filter out containers already assigned to a slot
      const usedIds = new Set((view.config.slots || []).filter(Boolean));
      setAvailableContainers(data.filter((e) => !usedIds.has(e.id)));
    } catch (err) {
      console.error("Failed to load containers:", err);
      toast.error("Failed to load containers");
    } finally {
      setLoadingContainers(false);
    }
  };

  const handleSlotClick = (position, slot) => {
    if (editMode) {
      setSelectedSlot({ position, slot });
      loadAvailableContainers();
    } else if (slot?.entity_id) {
      navigate(`/entity/${slot.entity_id}`);
    }
  };

  const handleAssignContainer = async (entityId) => {
    try {
      await views.updateSlot(id, selectedSlot.position, entityId);
      setSelectedSlot(null);
      loadView();
    } catch (err) {
      console.error("Failed to assign container:", err);
      toast.error("Failed to assign container");
    }
  };

  const handleClearSlot = async () => {
    try {
      await views.updateSlot(id, selectedSlot.position, null);
      setSelectedSlot(null);
      loadView();
    } catch (err) {
      console.error("Failed to clear slot:", err);
      toast.error("Failed to clear slot");
    }
  };

  const handleSaveLayout = async () => {
    const newRows = parseInt(editRows, 10);
    const newCols = parseInt(editCols, 10);
    if (!editName.trim() || !newRows || newRows < 1 || !newCols || newCols < 1) {
      toast.error("Enter a name and a valid grid size");
      return;
    }
    try {
      // Truncate slot assignments that no longer fit the new grid
      const slots = (view.config.slots || []).slice(0, newRows * newCols);
      await views.update(id, {
        name: editName.trim(),
        config: { ...view.config, rows: newRows, cols: newCols, slots },
      });
      toast.success("View updated");
      loadView();
    } catch (err) {
      console.error("Failed to update view:", err);
      toast.error("Failed to update view");
    }
  };

  const getSlotAtPosition = (position) => {
    // The worker stores config.slots as a flat array of entity ids
    // indexed by position, padded with nulls
    const entityId = view?.config?.slots?.[position];
    if (!entityId) return null;
    return { entity_id: entityId, entity: containersById[entityId] };
  };

  if (loading) {
    return (
      <div className="container text-center" style={{ paddingTop: "3rem" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="container text-center" style={{ paddingTop: "3rem" }}>
        <h2>View not found</h2>
        <button
          onClick={() => navigate("/views")}
          className="btn btn-primary mt-3"
        >
          Back to Views
        </button>
      </div>
    );
  }

  const { rows, cols } = view.config;
  const totalSlots = rows * cols;

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/views")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">{view.name}</h1>
            <p className="header-subtitle">
              {cols} x {rows} grid{view.parent_name && ` - ${view.parent_name}`}
            </p>
          </div>
          <div className="header-actions-right">
            <button
              onClick={() => {
                if (!editMode) {
                  setEditName(view.name);
                  setEditRows(String(rows));
                  setEditCols(String(cols));
                }
                setEditMode(!editMode);
              }}
              className="btn btn-ghost btn-icon"
              title={editMode ? "Done editing" : "Edit layout"}
            >
              {editMode ? <CheckIcon size={20} /> : <PencilIcon size={20} />}
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Rename / resize panel */}
        {editMode && (
          <div
            className="card"
            style={{
              maxWidth: "600px",
              margin: "0 auto var(--spacing-md)",
            }}
          >
            <label>Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="mb-2"
            />
            <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
              <div style={{ flex: 1 }}>
                <label>Columns</label>
                <input
                  type="number"
                  value={editCols}
                  onChange={(e) => setEditCols(e.target.value)}
                  min="1"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>Rows</label>
                <input
                  type="number"
                  value={editRows}
                  onChange={(e) => setEditRows(e.target.value)}
                  min="1"
                />
              </div>
            </div>
            <button
              onClick={handleSaveLayout}
              className="btn btn-primary"
              style={{ width: "100%", marginTop: "var(--spacing-sm)" }}
            >
              Save Layout
            </button>
          </div>
        )}

        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: "var(--spacing-sm)",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          {Array.from({ length: totalSlots }).map((_, index) => {
            const slot = getSlotAtPosition(index);
            const hasEntity = slot?.entity_id;

            return (
              <div
                key={index}
                onClick={() => handleSlotClick(index, slot)}
                style={{
                  aspectRatio: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "var(--spacing-sm)",
                  borderRadius: "var(--radius-md)",
                  cursor: editMode || hasEntity ? "pointer" : "default",
                  transition: "all 0.15s ease",
                  border: hasEntity
                    ? "2px solid var(--border-color)"
                    : "2px dashed var(--border-color)",
                  background: hasEntity
                    ? "var(--bg-card)"
                    : "var(--bg-secondary)",
                  ...(editMode && {
                    boxShadow: "0 0 0 2px var(--accent-primary-light)",
                  }),
                }}
              >
                {hasEntity ? (
                  <>
                    <div
                      style={{
                        marginBottom: "0.25rem",
                        color: "var(--accent-primary)",
                      }}
                    >
                      <ContainerIcon size={32} />
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {slot.entity?.name || "Container"}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                    }}
                  >
                    {editMode ? <PlusIcon size={20} /> : "---"}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div
          className="text-center text-muted"
          style={{
            marginTop: "var(--spacing-lg)",
            fontSize: "0.875rem",
          }}
        >
          {editMode
            ? "Tap slots to assign or remove containers"
            : "Tap a container to view details"}
        </div>
      </div>

      {/* Slot Assignment Modal */}
      {selectedSlot !== null && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedSlot(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSelectedSlot(null);
            }
          }}
          tabIndex={0}
          ref={(el) => el?.focus()}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Slot {selectedSlot.position + 1}
                {selectedSlot.slot?.entity?.name &&
                  ` - ${selectedSlot.slot.entity.name}`}
              </h2>
              <button
                onClick={() => setSelectedSlot(null)}
                className="btn btn-ghost btn-icon"
              >
                <CloseIcon size={20} />
              </button>
            </div>
            <div className="modal-body">
              {selectedSlot.slot?.entity_id && (
                <div style={{ marginBottom: "var(--spacing-md)" }}>
                  <button
                    onClick={handleClearSlot}
                    className="btn btn-danger"
                    style={{ width: "100%" }}
                  >
                    Remove from slot
                  </button>
                </div>
              )}

              <h4 style={{ marginBottom: "var(--spacing-sm)" }}>
                {selectedSlot.slot?.entity_id
                  ? "Replace with:"
                  : "Assign container:"}
              </h4>

              {loadingContainers ? (
                <div className="text-center">
                  <div className="spinner"></div>
                </div>
              ) : availableContainers.length === 0 ? (
                <p className="text-muted">
                  No available containers.{" "}
                  <span
                    onClick={() => navigate("/create?type=container")}
                    style={{
                      color: "var(--accent-primary)",
                      cursor: "pointer",
                    }}
                  >
                    Create one
                  </span>
                </p>
              ) : (
                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {availableContainers.map((container) => (
                    <div
                      key={container.id}
                      onClick={() => handleAssignContainer(container.id)}
                      className="list-item"
                      style={{
                        padding: "var(--spacing-sm) var(--spacing-md)",
                        cursor: "pointer",
                        borderRadius: "var(--radius-sm)",
                        marginBottom: "var(--spacing-xs)",
                      }}
                    >
                      <div className="list-item-icon">
                        <ContainerIcon size={20} />
                      </div>
                      <div className="list-item-content">
                        <div className="list-item-title">{container.name}</div>
                        {container.container_type && (
                          <div className="list-item-subtitle">
                            {container.container_type}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomViewDetail;
